import { buildSystemOverviewDescription } from "./dtoolsReviewNavigation";
import { isGenericOptionOverview } from "./dtoolsCustomerOptionCopy";
import { isDtoolsInternalImportSystemName } from "./dtoolsInternalSystems";
import { stripRoomsIncludedFromOverview } from "./dtoolsSystemOverviewQuotedItems";
import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";
import { buildFallbackSystemHighlights, isWeakCustomerHighlight } from "./dtoolsSystemHighlights";

export type DtoolsSystemContent = {
  overviewOriginal?: string;
  overviewEdited?: string | null;
  overviewLastAiPolishedAt?: string | null;
  highlightsOriginal?: string[];
  highlightsEdited?: string[] | null;
  highlightsLastGeneratedAt?: string | null;
  /** @deprecated Phase 2.5 — use proposal-level `dtoolsProposalPresentation.proposalDisclaimers`. */
  selectedDisclaimerIds?: number[];
};

export type DtoolsSystemOverviewRender = {
  body: string;
};

export type SalesDisclaimerRecord = {
  id: number;
  name: string;
  category: string;
  content: string;
  isActive: boolean;
};

export function systemContentKey(systemId: string | null | undefined, systemName: string): string {
  const id = String(systemId ?? "").trim();
  if (id) return id;
  return systemName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

/** Matches buildReviewHierarchy / revise normalization for system-name keys. */
export function normalizeDtoolsSystemNameForKey(systemName: string | null | undefined): string {
  return String(systemName ?? "").trim() || "Unassigned system";
}

export type DtoolsSystemContentKeyInput =
  | Pick<DtoolsReviewSystem, "systemId" | "name">
  | { systemId?: string | null; name?: string | null; systemName?: string | null };

function readDtoolsSystemNameFromKeyInput(input: DtoolsSystemContentKeyInput): string {
  if ("name" in input && input.name != null) return normalizeDtoolsSystemNameForKey(input.name);
  if ("systemName" in input && input.systemName != null) {
    return normalizeDtoolsSystemNameForKey(input.systemName);
  }
  return "Unassigned system";
}

/**
 * Canonical assembly/preview key for a D-Tools system.
 * Internal import placeholders always use name slugs (e.g. unassigned-system)
 * so null systemId and D-Tools placeholder ids like "unassigned" resolve to one key.
 */
export function resolveDtoolsSystemContentKey(input: DtoolsSystemContentKeyInput): string {
  const name = readDtoolsSystemNameFromKeyInput(input);
  if (isDtoolsInternalImportSystemName(name)) {
    return systemContentKey(null, name);
  }
  return systemContentKey(input.systemId ?? null, name);
}

/** Legacy keys that may appear in saved metadata for the same internal import system. */
export function dtoolsSystemContentKeyAliases(input: DtoolsSystemContentKeyInput): string[] {
  const name = readDtoolsSystemNameFromKeyInput(input);
  const canonical = resolveDtoolsSystemContentKey(input);
  if (!isDtoolsInternalImportSystemName(name)) return [canonical];
  const aliases = new Set<string>([canonical, systemContentKey(input.systemId ?? null, name)]);
  const trimmedId = String(input.systemId ?? "").trim();
  if (trimmedId === "unassigned" || trimmedId === "__none__") aliases.add(trimmedId);
  if (trimmedId === "unknown") aliases.add(trimmedId);
  return [...aliases];
}

/** Normalize legacy visibility keys persisted before canonical internal-system slugs. */
export function normalizeStoredDtoolsVisibilityKey(key: string): string {
  const trimmed = String(key ?? "").trim();
  if (!trimmed) return trimmed;
  if (trimmed === "unassigned" || trimmed === "__none__") {
    return systemContentKey(null, "Unassigned system");
  }
  if (trimmed === "unknown") return systemContentKey(null, "Unknown system");
  return trimmed;
}

export function normalizeStoredDtoolsVisibilityKeys(keys: string[] | undefined): string[] | undefined {
  if (!keys?.length) return keys;
  return [...new Set(keys.map(normalizeStoredDtoolsVisibilityKey).filter(Boolean))];
}

function overviewReferencesStaleOptionLabel(text: string, displayName: string): boolean {
  const label = String(displayName ?? "").trim();
  if (!label || /^option\s+\d+$/i.test(label)) return false;
  const body = String(text ?? "").trim();
  if (!body) return false;
  if (body.toLowerCase().includes(label.toLowerCase())) return false;
  return /\boption\s+\d+\b/i.test(body);
}

export function shouldRegenerateSystemOverviewOriginal(
  existing: DtoolsSystemContent | undefined,
  displayName: string,
): boolean {
  if (existing?.overviewEdited?.trim()) return false;
  const prior = existing?.overviewOriginal?.trim();
  if (!prior) return true;
  if (isGenericOptionOverview(prior)) return true;
  return overviewReferencesStaleOptionLabel(prior, displayName);
}

export function readDtoolsSystemContentMap(parsedMetadataJson: unknown): Record<string, DtoolsSystemContent> {
  if (!parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)) {
    return {};
  }
  const raw = (parsedMetadataJson as Record<string, unknown>).dtoolsSystemContent;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, DtoolsSystemContent> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    const v = value as Record<string, unknown>;
    const ids = Array.isArray(v.selectedDisclaimerIds)
      ? v.selectedDisclaimerIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : undefined;
    const highlightsOriginal = Array.isArray(v.highlightsOriginal)
      ? v.highlightsOriginal.map((h) => String(h).trim()).filter(Boolean)
      : undefined;
    const highlightsEdited = Array.isArray(v.highlightsEdited)
      ? v.highlightsEdited.map((h) => String(h).trim()).filter(Boolean)
      : v.highlightsEdited === null
        ? null
        : undefined;
    out[key] = {
      overviewOriginal: typeof v.overviewOriginal === "string" ? v.overviewOriginal : undefined,
      overviewEdited: typeof v.overviewEdited === "string" ? v.overviewEdited : v.overviewEdited === null ? null : undefined,
      overviewLastAiPolishedAt:
        typeof v.overviewLastAiPolishedAt === "string" ? v.overviewLastAiPolishedAt : undefined,
      highlightsOriginal,
      highlightsEdited,
      highlightsLastGeneratedAt:
        typeof v.highlightsLastGeneratedAt === "string" ? v.highlightsLastGeneratedAt : undefined,
      selectedDisclaimerIds: ids,
    };
  }
  return out;
}

export function mergeDtoolsSystemContentMap(
  parsedMetadataJson: unknown,
  patch: Record<string, DtoolsSystemContent>,
): Record<string, unknown> {
  const base =
    parsedMetadataJson && typeof parsedMetadataJson === "object" && !Array.isArray(parsedMetadataJson)
      ? { ...(parsedMetadataJson as Record<string, unknown>) }
      : {};
  const existing = readDtoolsSystemContentMap(base);
  return {
    ...base,
    dtoolsSystemContent: { ...existing, ...patch },
  };
}

export function ensureSystemOverviewOriginal(
  systemName: string,
  roomNames: string[],
  existing?: DtoolsSystemContent,
  system?: DtoolsReviewSystem,
): DtoolsSystemContent {
  const generated = buildSystemOverviewDescription(systemName, roomNames, system);
  if (existing?.overviewEdited?.trim()) {
    return {
      ...existing,
      overviewOriginal: existing.overviewOriginal?.trim() || generated,
    };
  }
  const prior = existing?.overviewOriginal?.trim();
  const overviewOriginal =
    !prior || shouldRegenerateSystemOverviewOriginal(existing, systemName) ? generated : prior;
  return {
    ...existing,
    overviewOriginal,
    overviewEdited: null,
  };
}

export function resolveSystemOverviewBody(
  content: DtoolsSystemContent | undefined,
  fallbackGenerated: string,
  displayName?: string,
): string {
  const edited = content?.overviewEdited?.trim();
  if (edited) {
    const body = isGenericOptionOverview(edited) ? fallbackGenerated : edited;
    return stripRoomsIncludedFromOverview(body);
  }
  const original = content?.overviewOriginal?.trim();
  const label = String(displayName ?? "").trim();
  const staleLabel = label ? overviewReferencesStaleOptionLabel(original ?? "", label) : false;
  const raw =
    original && !isGenericOptionOverview(original) && !staleLabel ? original : fallbackGenerated;
  return stripRoomsIncludedFromOverview(raw);
}

export function resolveSystemHighlights(
  content: DtoolsSystemContent | undefined,
  fallbackGenerated: string[],
): string[] {
  const edited = content?.highlightsEdited;
  if (edited && edited.length > 0) {
    return edited.map((h) => h.trim()).filter(Boolean).filter((h) => !isWeakCustomerHighlight(h)).slice(0, 6);
  }
  const original = content?.highlightsOriginal;
  if (original && original.length > 0) {
    return original.map((h) => h.trim()).filter(Boolean).filter((h) => !isWeakCustomerHighlight(h)).slice(0, 6);
  }
  return fallbackGenerated.map((h) => h.trim()).filter(Boolean).filter((h) => !isWeakCustomerHighlight(h)).slice(0, 6);
}

export function buildSystemHighlightsRender(input: {
  systemId: string | null;
  systemName: string;
  system: DtoolsReviewSystem;
  systemContentMap: Record<string, DtoolsSystemContent>;
}): string[] {
  const key = systemContentKey(input.systemId, input.systemName);
  const content = input.systemContentMap[key];
  const fallback = buildFallbackSystemHighlights(input.system);
  return resolveSystemHighlights(content, fallback);
}

export function buildSystemOverviewRender(input: {
  systemId: string | null;
  systemName: string;
  roomNames: string[];
  system: DtoolsReviewSystem;
  systemContentMap: Record<string, DtoolsSystemContent>;
}): DtoolsSystemOverviewRender {
  const key = systemContentKey(input.system.systemId, input.system.name);
  const generated = buildSystemOverviewDescription(input.systemName, input.roomNames, input.system);
  const content = ensureSystemOverviewOriginal(input.systemName, input.roomNames, input.systemContentMap[key], input.system);
  return {
    body: resolveSystemOverviewBody(content, generated, input.systemName),
  };
}

/** @deprecated Phase 2.5 — disclaimers are proposal-level copies. */
export function collectDisclaimerIdsFromSystemContent(
  systemContentMap: Record<string, DtoolsSystemContent>,
): number[] {
  const ids = new Set<number>();
  for (const content of Object.values(systemContentMap)) {
    for (const id of content.selectedDisclaimerIds ?? []) {
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
  }
  return [...ids];
}

/** Simple word-level diff for AI polish preview (no external deps). */
export function diffTextPreview(before: string, after: string): Array<{ type: "same" | "removed" | "added"; text: string }> {
  const a = before.split(/(\s+)/);
  const b = after.split(/(\s+)/);
  const out: Array<{ type: "same" | "removed" | "added"; text: string }> = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      out.push({ type: "same", text: a[i]! });
      i += 1;
      j += 1;
    } else if (j < b.length && (i >= a.length || !a.slice(i).includes(b[j]!))) {
      out.push({ type: "added", text: b[j]! });
      j += 1;
    } else if (i < a.length) {
      out.push({ type: "removed", text: a[i]! });
      i += 1;
    }
  }
  return out;
}
